import {isAiBundle, bundleMetadata} from '@lib/messarioAi/storage';
import {
  MessarioAiDraftResponse,
  MessarioAiGenerateBundle,
  MessarioAiGenerateDraftPayload,
  MessarioAiMemory,
  MessarioAiMemoryOperation,
  MessarioAiTrainingCaseResponse,
  MessarioAiTrainingChange
} from '@lib/messarioAi/types';

export class MessarioAiFetchFailure extends Error {
  constructor(
    public readonly kind: 'configuration' | 'network' | 'rejected' | 'invalid-response' | 'timeout',
    message: string,
    public readonly status?: number,
    public readonly errorCode?: string,
    public readonly source?: 'openai' | 'panel'
  ) {
    super(message);
    this.name = 'MessarioAiFetchFailure';
  }
}

function panelOriginNormalized(panelOrigin: string) {
  return panelOrigin.replace(/\/+$/, '');
}

async function readJsonBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return await response.json() as Record<string, unknown>;
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an unreadable AI response.',
      response.status
    );
  }
}

function assertDraftResponse(body: Record<string, unknown>, status: number): MessarioAiDraftResponse {
  if(
    body.config_schema_version !== 1 ||
    typeof body.variable_definitions_revision !== 'number' ||
    typeof body.config_hash !== 'string' ||
    typeof body.reply !== 'string' ||
    typeof body.draft_generate_id !== 'number' ||
    (body.training_event_id !== null && typeof body.training_event_id !== 'number') ||
    typeof body.prompt_revision !== 'number'
  ) {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete AI draft response.',
      status
    );
  }

  return {
    config_schema_version: 1,
    variable_definitions_revision: body.variable_definitions_revision as number,
    config_hash: body.config_hash as string,
    reply: body.reply,
    draft_generate_id: body.draft_generate_id,
    training_event_id: body.training_event_id as number | null,
    prompt_revision: body.prompt_revision,
    usage: (body.usage && typeof body.usage === 'object' ?
      body.usage :
      {
        model: '',
        external_input_tokens: 0,
        external_output_tokens: 0,
        ai_tokens: 0,
        balance_after: 0
      }) as MessarioAiDraftResponse['usage']
  };
}

function requirePanelAuth(token: string, panelOrigin: string) {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.',
      undefined,
      undefined,
      'panel'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.',
      undefined,
      undefined,
      'panel'
    );
  }
}

async function panelFetchJson(
  token: string,
  panelOrigin: string,
  path: string,
  init: RequestInit,
  fetchValue: typeof fetch
): Promise<{response: Response, body: Record<string, unknown>}> {
  requirePanelAuth(token, panelOrigin);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string> | undefined)
  };
  if(init.body != null) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}${path}`,
      {
        ...init,
        headers
      }
    );
  } catch{
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for AI draft.',
      undefined,
      undefined,
      'panel'
    );
  }

  const body = await readJsonBody(response);
  return {response, body};
}

function rejectedPanelFailure(body: Record<string, unknown>, status: number, fallback: string) {
  const errorCode = typeof body.error === 'string' ? body.error : undefined;
  return new MessarioAiFetchFailure(
    'rejected',
    (typeof body.message === 'string' && body.message) ||
      errorCode ||
      fallback,
    status,
    errorCode,
    'panel'
  );
}

export async function fetchMessarioAiGenerateBundle(
  token: string,
  panelOrigin: string,
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiGenerateBundle> {
  const {response, body} = await panelFetchJson(
    token,
    panelOrigin,
    '/api/telegram-web/ai/bundle',
    {method: 'GET'},
    fetchValue
  );

  if(!response.ok) {
    throw rejectedPanelFailure(body, response.status, 'Messario rejected the AI bundle request.');
  }

  if(!isAiBundle(body)) {
    throw new MessarioAiFetchFailure('invalid-response', 'Настройки AI имеют устаревший формат. Откройте Telegram заново из Panel.', response.status, 'configuration_format_changed', 'panel');
  }
  return bundleMetadata(body);
}

export async function requestMessarioAiGenerate(
  token: string,
  panelOrigin: string,
  payload: MessarioAiGenerateDraftPayload,
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiDraftResponse> {
  const bodyPayload: MessarioAiGenerateDraftPayload = {
    config_schema_version: payload.config_schema_version,
    peer_id: payload.peer_id,
    messages: payload.messages,
    prompt_revision: payload.prompt_revision,
    variable_definitions_revision: payload.variable_definitions_revision,
    model: payload.model,
    client_attempt_id: payload.client_attempt_id
  };
  if(payload.one_shot_instruction) {
    bodyPayload.one_shot_instruction = payload.one_shot_instruction;
  }

  const {response, body} = await panelFetchJson(
    token,
    panelOrigin,
    '/api/telegram-web/ai/generate',
    {method: 'POST', body: JSON.stringify(bodyPayload)},
    fetchValue
  );

  if(!response.ok) {
    throw rejectedPanelFailure(body, response.status, 'Messario rejected the AI generate request.');
  }

  return assertDraftResponse(body, response.status);
}

export async function completeMessarioAiTrainingEvent(
  token: string,
  panelOrigin: string,
  trainingEventId: number,
  payload: {final_reply_text: string, sent_message_id: string},
  fetchValue: typeof fetch = fetch
): Promise<void> {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.'
    );
  }

  if(!trainingEventId) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario AI training event id is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/ai/training-events/${trainingEventId}/complete`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for AI training complete.'
    );
  }

  if(!response.ok) {
    throw new MessarioAiFetchFailure(
      'rejected',
      'Messario rejected the AI training complete request.',
      response.status
    );
  }
}

export async function dismissMessarioAiTrainingEvent(
  token: string,
  panelOrigin: string,
  trainingEventId: number,
  fetchValue: typeof fetch = fetch
): Promise<void> {
  if(!panelOrigin || !token || !trainingEventId) {
    return;
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/ai/training-events/${trainingEventId}/dismiss`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: '{}'
      }
    );
  } catch(err) {
    console.warn('Messario AI training dismiss failed', err);
    return;
  }

  if(!response.ok) {
    console.warn('Messario AI training dismiss rejected', response.status);
  }
}

function assertTrainingCaseResponse(
  body: Record<string, unknown>,
  status: number
): MessarioAiTrainingCaseResponse {
  const casePayload = body.case;
  if(!casePayload || typeof casePayload !== 'object') {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete training case response.',
      status
    );
  }

  const c = casePayload as Record<string, unknown>;
  if(typeof c.id !== 'number' || typeof c.status !== 'string' || typeof c.draft_generate_id !== 'number') {
    throw new MessarioAiFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete training case payload.',
      status
    );
  }

  return {
    case: c as MessarioAiTrainingCaseResponse['case'],
    analyzer_result: (body.analyzer_result && typeof body.analyzer_result === 'object' ?
      body.analyzer_result as Record<string, unknown> :
      (c.analyzer_result as Record<string, unknown> | null | undefined) ?? null),
    proposed_changes: (Array.isArray(body.proposed_changes) ?
      body.proposed_changes as MessarioAiTrainingChange[] :
      (Array.isArray(c.proposed_changes) ? c.proposed_changes as MessarioAiTrainingChange[] : null)),
    conflict: body.conflict === true
  };
}

async function postMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  path: string,
  payload: Record<string, unknown>,
  fetchValue: typeof fetch
): Promise<MessarioAiTrainingCaseResponse> {
  if(!panelOrigin) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioAiFetchFailure(
      'configuration',
      'Messario chat access token is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}${path}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
  } catch(err) {
    throw new MessarioAiFetchFailure(
      'network',
      'Could not reach Messario Panel for training case.'
    );
  }

  const body = await readJsonBody(response);
  // 409 conflict still returns a case payload — surface it to the caller.
  if(!response.ok && (response.status !== 409 || !body.case)) {
    throw new MessarioAiFetchFailure(
      'rejected',
      (typeof body.message === 'string' && body.message) ||
        (typeof body.error === 'string' && body.error) ||
        'Messario rejected the training case request.',
      response.status
    );
  }

  return assertTrainingCaseResponse(body, response.status);
}

export async function createMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  payload: {
    draft_generate_id: number,
    operator_feedback: string,
    expected_reply?: string,
    user_message?: string
  },
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiTrainingCaseResponse> {
  return postMessarioAiTrainingCase(
    token,
    panelOrigin,
    '/api/telegram-web/ai/training-cases',
    payload,
    fetchValue
  );
}

export async function applyMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  caseId: number,
  payload: {proposed_changes?: MessarioAiTrainingChange[]} = {},
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiTrainingCaseResponse> {
  return postMessarioAiTrainingCase(
    token,
    panelOrigin,
    `/api/telegram-web/ai/training-cases/${caseId}/apply`,
    payload,
    fetchValue
  );
}

export async function rejectMessarioAiTrainingCase(
  token: string,
  panelOrigin: string,
  caseId: number,
  payload: {reason?: string} = {},
  fetchValue: typeof fetch = fetch
): Promise<MessarioAiTrainingCaseResponse> {
  return postMessarioAiTrainingCase(
    token,
    panelOrigin,
    `/api/telegram-web/ai/training-cases/${caseId}/reject`,
    payload,
    fetchValue
  );
}

export async function fetchMessarioAiMemory(token: string, origin: string, peerId: string): Promise<MessarioAiMemory> {
  const {response, body} = await panelFetchJson(token, origin, `/api/telegram-web/ai/memory?peer_id=${encodeURIComponent(peerId)}`, {method: 'GET'}, fetch);
  if(!response.ok) throw rejectedPanelFailure(body, response.status, 'Не удалось загрузить память.');
  if(body.config_schema_version !== 1 || !Array.isArray(body.fields)) throw new MessarioAiFetchFailure('invalid-response', 'Обновите страницу: формат памяти изменился.');
  return body as unknown as MessarioAiMemory;
}
export async function patchMessarioAiMemory(token: string, origin: string, peerId: string, memory: MessarioAiMemory, operations: MessarioAiMemoryOperation[]): Promise<MessarioAiMemory> {
  const {response, body} = await panelFetchJson(token, origin, '/api/telegram-web/ai/memory', {method: 'PATCH', body: JSON.stringify({peer_id: peerId, config_schema_version: memory.config_schema_version, prompt_revision: memory.prompt_revision, variable_definitions_revision: memory.variable_definitions_revision, expected_state_revision: memory.state_revision, operations})}, fetch);
  if(!response.ok) throw rejectedPanelFailure(body, response.status, 'Не удалось сохранить память.');
  return body as unknown as MessarioAiMemory;
}
